import { useEffect, useMemo, useState } from 'react';
import {
  FileText, Edit2, Search, DollarSign, Percent, Calendar, X,
  ChevronLeft, ChevronRight, Download, Eye, Trash2, Archive,
  ChevronDown, User,
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAllRows';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

type PayType = 'salary' | 'percentage' | 'hourly';
type PayFrequency = 'weekly' | 'biweekly' | 'monthly';

interface HoursEntry {
  date: string; // ISO yyyy-mm-dd
  hours: number;
}

interface Employee {
  id: string;
  full_name: string;
  role: string;
  pay_type: PayType | null;
  base_salary: number | null;
  commission_percent: number | null;
  hourly_rate: number | null;
  pay_frequency: PayFrequency | null;
  pay_company_name: string | null;
  paying_company_id: string | null;
}

interface Company {
  id: string;
  name: string;
  code: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  phone: string | null;
  email: string | null;
}

interface BonusRow {
  id: string;
  profile_id: string;
  amount: number;
  description: string | null;
  bonus_date: string;
}

interface ExpenseRow {
  id: string;
  profile_id: string;
  amount: number;
  description: string | null;
  expense_date: string;
}

interface DispatchRow {
  dispatch_date: string;
  loads: {
    total_rate: number | null;
    created_by: string | null;
    status: string | null;
    load_number: string | null;
  } | null;
}

interface LineItemSnapshot {
  id: string;
  date: string;
  description: string | null;
  amount: number;
}

interface CompanySnapshot {
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  phone: string | null;
  email: string | null;
}

interface GeneratedStatement {
  id: string;
  statement_number: number;
  profile_id: string | null;
  company_id: string | null;
  employee_name: string;
  employee_role: string | null;
  employee_company_name: string | null;
  company_snapshot: CompanySnapshot | null;
  period_start: string;
  period_end: string;
  pay_frequency: PayFrequency;
  pay_type: PayType;
  base_salary: number | null;
  commission_percent: number | null;
  hourly_rate: number | null;
  total_hours: number | null;
  hours_snapshot: HoursEntry[];
  gross_amount: number;
  base_amount: number;
  total_bonuses: number;
  total_expenses: number;
  net_pay: number;
  bonuses_snapshot: LineItemSnapshot[];
  expenses_snapshot: LineItemSnapshot[];
  created_at: string;
}

const EMPLOYEE_ROLES = [
  'Superadmin', 'Admin',
  'Dispatch', 'Dispatch Manager',
  'Safety', 'Safety Manager',
  'Accounting', 'Accounting Manager',
  'Recruiting', 'Afterhours', 'ELD',
];

const DISPATCHER_ROLES = new Set(['Dispatch', 'Dispatch Manager']);

function startOfWeek(d: Date): Date {
  const x = new Date(d);
  const day = x.getDay();
  const diff = x.getDate() - day + (day === 0 ? -6 : 1);
  x.setDate(diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function startOfMonth(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), 1);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfMonth(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  x.setHours(23, 59, 59, 999);
  return x;
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function fmtRange(a: Date, b: Date): string {
  return `${a.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${b.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

function computePeriod(anchor: Date, freq: PayFrequency): { start: Date; end: Date } {
  if (freq === 'weekly') {
    const start = startOfWeek(anchor);
    const end = addDays(start, 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }
  if (freq === 'biweekly') {
    const start = startOfWeek(anchor);
    const end = addDays(start, 13);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }
  return { start: startOfMonth(anchor), end: endOfMonth(anchor) };
}

function shiftPeriod(anchor: Date, freq: PayFrequency, direction: -1 | 1): Date {
  if (freq === 'weekly') return addDays(anchor, 7 * direction);
  if (freq === 'biweekly') return addDays(anchor, 14 * direction);
  const d = new Date(anchor);
  d.setMonth(d.getMonth() + direction);
  return d;
}

export function EmployeeStatementsView() {
  const toast = useToast();
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<'employees' | 'generated'>('employees');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Generated statements
  const [statements, setStatements] = useState<GeneratedStatement[]>([]);
  const [statementsLoading, setStatementsLoading] = useState(false);
  const [statementsSearch, setStatementsSearch] = useState('');
  const [generatedView, setGeneratedView] = useState<'week' | 'month'>('week');
  const [collapsedEmployees, setCollapsedEmployees] = useState<Set<string>>(new Set());

  // Pay config modal
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configEmp, setConfigEmp] = useState<Employee | null>(null);
  const [configForm, setConfigForm] = useState({
    pay_type: 'salary' as PayType,
    base_salary: '',
    commission_percent: '',
    hourly_rate: '',
    pay_frequency: 'weekly' as PayFrequency,
    pay_company_name: '',
    paying_company_id: '',
  });

  // Generate-statement modal
  const [showGenModal, setShowGenModal] = useState(false);
  const [genEmployeeId, setGenEmployeeId] = useState<string>('');
  const [genEmployeeSearch, setGenEmployeeSearch] = useState<string>('');
  const [genEmployeeOpen, setGenEmployeeOpen] = useState<boolean>(false);
  const [genCompanyId, setGenCompanyId] = useState<string>('');
  const [genCadence, setGenCadence] = useState<PayFrequency>('weekly');
  const [genAnchor, setGenAnchor] = useState<Date>(() => new Date());
  const [genBonuses, setGenBonuses] = useState<BonusRow[]>([]);
  const [genExpenses, setGenExpenses] = useState<ExpenseRow[]>([]);
  const [selectedBonusIds, setSelectedBonusIds] = useState<Set<string>>(new Set());
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<Set<string>>(new Set());
  const [genGross, setGenGross] = useState<number>(0);
  const [genHours, setGenHours] = useState<Record<string, string>>({}); // iso date → hours string
  const [loadingItems, setLoadingItems] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => { loadEmployees(); loadCompanies(); loadStatements(); }, []);

  async function loadEmployees() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, role, pay_type, base_salary, commission_percent, hourly_rate, pay_frequency, pay_company_name, paying_company_id')
        .in('role', EMPLOYEE_ROLES)
        .order('full_name');
      if (error) throw error;
      setEmployees((data || []) as Employee[]);
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  }

  async function loadCompanies() {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, code, address, city, state, zip_code, phone, email')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      setCompanies((data || []) as Company[]);
    } catch (err: any) {
      console.error('Failed to load companies', err);
    }
  }

  async function loadStatements() {
    try {
      setStatementsLoading(true);
      const { data, error } = await supabase
        .from('employee_statements')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setStatements((data || []) as GeneratedStatement[]);
    } catch (err: any) {
      console.error('Failed to load statements', err);
    } finally {
      setStatementsLoading(false);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(e =>
      e.full_name.toLowerCase().includes(q) || e.role.toLowerCase().includes(q)
    );
  }, [employees, search]);

  const genEmployee = useMemo(
    () => employees.find(e => e.id === genEmployeeId) || null,
    [employees, genEmployeeId]
  );
  const genCompany = useMemo(
    () => companies.find(c => c.id === genCompanyId) || null,
    [companies, genCompanyId]
  );
  const genPeriod = useMemo(
    () => computePeriod(genAnchor, genCadence),
    [genAnchor, genCadence]
  );

  // Load bonuses/expenses/gross when employee or period changes in the Generate modal.
  useEffect(() => {
    if (!showGenModal || !genEmployee) return;
    let cancelled = false;
    // Reset per-day hours whenever employee or period changes; user re-enters them.
    setGenHours({});
    (async () => {
      try {
        setLoadingItems(true);
        setPdfUrl(null);
        const startIso = isoDate(genPeriod.start);
        const endIso = isoDate(genPeriod.end);
        const [bonusRes, expRes] = await Promise.all([
          supabase.from('employee_bonuses')
            .select('id, profile_id, amount, description, bonus_date')
            .eq('profile_id', genEmployee.id)
            .gte('bonus_date', startIso).lte('bonus_date', endIso)
            .order('bonus_date'),
          supabase.from('employee_expenses')
            .select('id, profile_id, amount, description, expense_date')
            .eq('profile_id', genEmployee.id)
            .gte('expense_date', startIso).lte('expense_date', endIso)
            .order('expense_date'),
        ]);
        if (cancelled) return;
        if (bonusRes.error) throw bonusRes.error;
        if (expRes.error) throw expRes.error;
        const b = (bonusRes.data || []) as BonusRow[];
        const x = (expRes.data || []) as ExpenseRow[];
        setGenBonuses(b);
        setGenExpenses(x);
        setSelectedBonusIds(new Set(b.map(r => r.id)));
        setSelectedExpenseIds(new Set(x.map(r => r.id)));

        if (genEmployee.pay_type === 'percentage') {
          const data = await fetchAllRows<any>(() => supabase
            .from('dispatches')
            .select('id, dispatch_date, loads(total_rate, created_by, status, load_number)')
            .gte('dispatch_date', startIso)
            .lte('dispatch_date', endIso));
          const rows = (data || []) as unknown as DispatchRow[];
          let gross = 0;
          rows.forEach(d => {
            const l = d.loads;
            if (!l || l.created_by !== genEmployee.id) return;
            const ln = l.load_number || '';
            if (l.status === 'removed' || ln.includes('_REMOVED_')) return;
            gross += Number(l.total_rate || 0);
          });
          if (!cancelled) setGenGross(gross);
        } else {
          if (!cancelled) setGenGross(0);
        }
      } catch (err: any) {
        console.error(err);
        if (!cancelled) toast.error(err.message || 'Failed to load items');
      } finally {
        if (!cancelled) setLoadingItems(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGenModal, genEmployeeId, genCadence, genAnchor]);

  function openConfig(e: Employee) {
    setConfigEmp(e);
    setConfigForm({
      pay_type: (e.pay_type as PayType) || (DISPATCHER_ROLES.has(e.role) ? 'percentage' : 'salary'),
      base_salary: e.base_salary != null ? String(e.base_salary) : '',
      commission_percent: e.commission_percent != null ? String(e.commission_percent) : '',
      hourly_rate: e.hourly_rate != null ? String(e.hourly_rate) : '',
      pay_frequency: (e.pay_frequency as PayFrequency) || 'weekly',
      pay_company_name: e.pay_company_name || '',
      paying_company_id: e.paying_company_id || '',
    });
    setShowConfigModal(true);
  }

  async function saveConfig() {
    if (!configEmp) return;
    const payload: any = {
      pay_type: configForm.pay_type,
      pay_frequency: configForm.pay_frequency,
      base_salary: configForm.pay_type === 'salary' && configForm.base_salary
        ? parseFloat(configForm.base_salary) : null,
      commission_percent: configForm.pay_type === 'percentage' && configForm.commission_percent
        ? parseFloat(configForm.commission_percent) : null,
      hourly_rate: configForm.pay_type === 'hourly' && configForm.hourly_rate
        ? parseFloat(configForm.hourly_rate) : null,
      pay_company_name: configForm.pay_company_name.trim() || null,
      paying_company_id: configForm.paying_company_id || null,
    };
    try {
      const { error } = await supabase.from('profiles').update(payload).eq('id', configEmp.id);
      if (error) throw error;
      toast.success('Pay config saved');
      setShowConfigModal(false);
      loadEmployees();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save');
    }
  }

  function openGenerate(prefillId?: string) {
    const emp = prefillId
      ? employees.find(e => e.id === prefillId)
      : null;
    setGenEmployeeId(emp?.id || '');
    setGenEmployeeSearch(emp ? `${emp.full_name} — ${emp.role}` : '');
    setGenEmployeeOpen(false);
    setGenCadence((emp?.pay_frequency as PayFrequency) || 'weekly');
    setGenAnchor(new Date());
    setGenBonuses([]);
    setGenExpenses([]);
    setSelectedBonusIds(new Set());
    setSelectedExpenseIds(new Set());
    setGenGross(0);
    setPdfUrl(null);
    setGenCompanyId(emp?.paying_company_id || companies[0]?.id || '');
    setShowGenModal(true);
  }

  function selectGenEmployee(emp: Employee) {
    setGenEmployeeId(emp.id);
    setGenEmployeeSearch(`${emp.full_name} — ${emp.role}`);
    setGenEmployeeOpen(false);
    if (emp.pay_frequency) setGenCadence(emp.pay_frequency as PayFrequency);
    if (emp.paying_company_id) setGenCompanyId(emp.paying_company_id);
    setPdfUrl(null);
  }

  function toggleBonus(id: string) {
    setSelectedBonusIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setPdfUrl(null);
  }

  function toggleExpense(id: string) {
    setSelectedExpenseIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setPdfUrl(null);
  }

  // Build the list of day iso strings covered by the current generate-modal period.
  const genPeriodDays = useMemo(() => {
    const days: string[] = [];
    const dayMs = 86400000;
    const start = new Date(genPeriod.start);
    start.setHours(0, 0, 0, 0);
    const end = new Date(genPeriod.end);
    end.setHours(0, 0, 0, 0);
    for (let t = start.getTime(); t <= end.getTime(); t += dayMs) {
      days.push(isoDate(new Date(t)));
    }
    return days;
  }, [genPeriod.start, genPeriod.end]);

  const genHoursEntries = useMemo<HoursEntry[]>(() => {
    return genPeriodDays
      .map(d => ({ date: d, hours: parseFloat(genHours[d] || '') || 0 }))
      .filter(e => e.hours > 0);
  }, [genPeriodDays, genHours]);

  const genTotalHours = useMemo(
    () => genHoursEntries.reduce((s, e) => s + e.hours, 0),
    [genHoursEntries]
  );

  function computeTotals() {
    const bonuses = genBonuses.filter(b => selectedBonusIds.has(b.id));
    const expenses = genExpenses.filter(x => selectedExpenseIds.has(x.id));
    const totalBonuses = bonuses.reduce((s, b) => s + Number(b.amount || 0), 0);
    const totalExpenses = expenses.reduce((s, x) => s + Number(x.amount || 0), 0);
    let baseAmount = 0;
    if (genEmployee?.pay_type === 'percentage') {
      baseAmount = genGross * (Number(genEmployee.commission_percent || 0) / 100);
    } else if (genEmployee?.pay_type === 'salary') {
      baseAmount = Number(genEmployee.base_salary || 0);
    } else if (genEmployee?.pay_type === 'hourly') {
      baseAmount = genTotalHours * Number(genEmployee.hourly_rate || 0);
    }
    const netPay = baseAmount + totalBonuses - totalExpenses;
    return { bonuses, expenses, totalBonuses, totalExpenses, baseAmount, netPay };
  }

  interface PdfInput {
    statementNumber?: number;
    employeeName: string;
    employeeRole: string | null;
    employeeCompany?: string | null;
    company: CompanySnapshot;
    periodStart: Date;
    periodEnd: Date;
    payType: PayType;
    payFrequency: PayFrequency;
    commissionPercent: number | null;
    hourlyRate: number | null;
    totalHours: number | null;
    hoursSnapshot: HoursEntry[];
    grossAmount: number;
    baseAmount: number;
    bonuses: LineItemSnapshot[];
    expenses: LineItemSnapshot[];
    totalBonuses: number;
    totalExpenses: number;
    netPay: number;
    generatedAt?: Date;
  }

  function renderPdf(input: PdfInput): jsPDF {
    const doc = new jsPDF();
    let y = 18;

    // Header
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(input.company.name, 14, y);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const companyLines = [
      input.company.address || '',
      [input.company.city, input.company.state, input.company.zip_code].filter(Boolean).join(', '),
      input.company.phone || '',
      input.company.email || '',
    ].filter(Boolean);
    companyLines.forEach((line, i) => {
      doc.text(line, 14, y + 6 + i * 5);
    });

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('EMPLOYEE STATEMENT', 196, y, { align: 'right' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    if (input.statementNumber) {
      doc.text(`#${input.statementNumber}`, 196, y + 6, { align: 'right' });
      doc.text(fmtRange(input.periodStart, input.periodEnd), 196, y + 12, { align: 'right' });
      doc.text(`Generated: ${(input.generatedAt || new Date()).toLocaleDateString()}`, 196, y + 18, { align: 'right' });
    } else {
      doc.text(fmtRange(input.periodStart, input.periodEnd), 196, y + 6, { align: 'right' });
      doc.text(`Generated: ${(input.generatedAt || new Date()).toLocaleDateString()}`, 196, y + 12, { align: 'right' });
    }

    y = Math.max(y + 6 + companyLines.length * 5, y + (input.statementNumber ? 24 : 18)) + 8;
    doc.setLineWidth(0.4);
    doc.line(14, y, 196, y);
    y += 8;

    // Pay to — contractor company name (when set) is the payee; person + role below.
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Pay To:', 14, y);
    if (input.employeeCompany) {
      doc.text(input.employeeCompany, 30, y);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(input.employeeName, 30, y + 5);
      if (input.employeeRole) {
        doc.text(input.employeeRole, 30, y + 10);
        y += 5;
      }
      y += 12;
    } else {
      doc.setFont('helvetica', 'normal');
      doc.text(input.employeeName, 30, y);
      if (input.employeeRole) {
        doc.setFontSize(9);
        doc.text(input.employeeRole, 30, y + 5);
      }
      y += 12;
    }

    // Base pay
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Base Pay', 14, y);
    y += 3;
    const baseBody: any[] = [];
    if (input.payType === 'percentage') {
      baseBody.push(['Dispatcher Gross (period)', '', `$${input.grossAmount.toFixed(2)}`]);
      baseBody.push([`Commission @ ${Number(input.commissionPercent || 0)}%`, '', `$${input.baseAmount.toFixed(2)}`]);
    } else if (input.payType === 'hourly') {
      baseBody.push([`Hours worked × $${Number(input.hourlyRate || 0).toFixed(2)}/hr`, `${Number(input.totalHours || 0).toFixed(2)} hrs`, `$${input.baseAmount.toFixed(2)}`]);
    } else {
      baseBody.push([`Salary (${input.payFrequency})`, '', `$${input.baseAmount.toFixed(2)}`]);
    }
    autoTable(doc, {
      startY: y,
      head: [['Description', '', 'Amount']],
      body: baseBody,
      theme: 'grid',
      headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: 'bold' },
      columnStyles: { 2: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    y = (doc as any).lastAutoTable.finalY + 8;

    // Hours breakdown — per day, only for hourly pay
    if (input.payType === 'hourly' && input.hoursSnapshot.length > 0) {
      if (y > 240) { doc.addPage(); y = 20; }
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Hours Worked', 14, y);
      y += 3;
      autoTable(doc, {
        startY: y,
        head: [['Date', 'Day', 'Hours']],
        body: input.hoursSnapshot.map(h => {
          const d = new Date(h.date + 'T00:00:00');
          return [
            d.toLocaleDateString(),
            d.toLocaleDateString('en-US', { weekday: 'long' }),
            Number(h.hours).toFixed(2),
          ];
        }),
        foot: [['Total', '', Number(input.totalHours || 0).toFixed(2)]],
        theme: 'grid',
        headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: 'bold' },
        footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
        columnStyles: { 2: { halign: 'right' } },
        margin: { left: 14, right: 14 },
      });
      y = (doc as any).lastAutoTable.finalY + 8;
    }

    // Bonuses
    if (input.bonuses.length > 0) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Bonuses', 14, y);
      y += 3;
      autoTable(doc, {
        startY: y,
        head: [['Date', 'Description', 'Amount']],
        body: input.bonuses.map(b => [
          b.date,
          b.description || '—',
          `$${Number(b.amount).toFixed(2)}`,
        ]),
        foot: [['Total', '', `$${input.totalBonuses.toFixed(2)}`]],
        theme: 'grid',
        headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: 'bold' },
        footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold', textColor: [22, 101, 52] },
        columnStyles: { 2: { halign: 'right' } },
        margin: { left: 14, right: 14 },
      });
      y = (doc as any).lastAutoTable.finalY + 8;
    }

    // Expenses
    if (input.expenses.length > 0) {
      if (y > 250) { doc.addPage(); y = 20; }
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Expenses', 14, y);
      y += 3;
      autoTable(doc, {
        startY: y,
        head: [['Date', 'Description', 'Amount']],
        body: input.expenses.map(x => [
          x.date,
          x.description || '—',
          `-$${Number(x.amount).toFixed(2)}`,
        ]),
        foot: [['Total', '', `-$${input.totalExpenses.toFixed(2)}`]],
        theme: 'grid',
        headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: 'bold' },
        footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold', textColor: [185, 28, 28] },
        columnStyles: { 2: { halign: 'right' } },
        margin: { left: 14, right: 14 },
      });
      y = (doc as any).lastAutoTable.finalY + 8;
    }

    // Net
    if (y > 260) { doc.addPage(); y = 20; }
    doc.setLineWidth(0.5);
    doc.line(14, y, 196, y);
    y += 8;
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Net Pay', 14, y);
    doc.text(`$${input.netPay.toFixed(2)}`, 196, y, { align: 'right' });

    return doc;
  }

  function buildPdfInput(): PdfInput | null {
    if (!genEmployee) {
      toast.error('Select an employee');
      return null;
    }
    if (!genEmployee.pay_type) {
      toast.error('Configure pay for this employee first');
      return null;
    }
    if (!genCompany) {
      toast.error('Select a paying company');
      return null;
    }
    if (genEmployee.pay_type === 'hourly' && genTotalHours <= 0) {
      toast.error('Enter hours worked for at least one day');
      return null;
    }
    const { bonuses, expenses, totalBonuses, totalExpenses, baseAmount, netPay } = computeTotals();
    return {
      employeeName: genEmployee.full_name,
      employeeRole: genEmployee.role,
      employeeCompany: genEmployee.pay_company_name,
      company: {
        name: genCompany.name,
        address: genCompany.address,
        city: genCompany.city,
        state: genCompany.state,
        zip_code: genCompany.zip_code,
        phone: genCompany.phone,
        email: genCompany.email,
      },
      periodStart: genPeriod.start,
      periodEnd: genPeriod.end,
      payType: genEmployee.pay_type,
      payFrequency: genCadence,
      commissionPercent: genEmployee.commission_percent,
      hourlyRate: genEmployee.pay_type === 'hourly' ? Number(genEmployee.hourly_rate || 0) : null,
      totalHours: genEmployee.pay_type === 'hourly' ? genTotalHours : null,
      hoursSnapshot: genEmployee.pay_type === 'hourly' ? genHoursEntries : [],
      grossAmount: genGross,
      baseAmount,
      bonuses: bonuses.map(b => ({ id: b.id, date: b.bonus_date, description: b.description, amount: Number(b.amount) })),
      expenses: expenses.map(x => ({ id: x.id, date: x.expense_date, description: x.description, amount: Number(x.amount) })),
      totalBonuses,
      totalExpenses,
      netPay,
    };
  }


  // Auto-refresh preview silently when the inputs change (no toasts on missing fields).
  useEffect(() => {
    if (!showGenModal) return;
    if (!genEmployee || !genEmployee.pay_type || !genCompany) {
      if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
      return;
    }
    if (loadingItems) return;
    const t = setTimeout(() => {
      try {
        const { bonuses, expenses, totalBonuses, totalExpenses, baseAmount, netPay } = computeTotals();
        const input: PdfInput = {
          employeeName: genEmployee.full_name,
          employeeRole: genEmployee.role,
          employeeCompany: genEmployee.pay_company_name,
          company: {
            name: genCompany.name,
            address: genCompany.address,
            city: genCompany.city,
            state: genCompany.state,
            zip_code: genCompany.zip_code,
            phone: genCompany.phone,
            email: genCompany.email,
          },
          periodStart: genPeriod.start,
          periodEnd: genPeriod.end,
          payType: genEmployee.pay_type!,
          payFrequency: genCadence,
          commissionPercent: genEmployee.commission_percent,
          hourlyRate: genEmployee.pay_type === 'hourly' ? Number(genEmployee.hourly_rate || 0) : null,
          totalHours: genEmployee.pay_type === 'hourly' ? genTotalHours : null,
          hoursSnapshot: genEmployee.pay_type === 'hourly' ? genHoursEntries : [],
          grossAmount: genGross,
          baseAmount,
          bonuses: bonuses.map(b => ({ id: b.id, date: b.bonus_date, description: b.description, amount: Number(b.amount) })),
          expenses: expenses.map(x => ({ id: x.id, date: x.expense_date, description: x.description, amount: Number(x.amount) })),
          totalBonuses,
          totalExpenses,
          netPay,
        };
        const doc = renderPdf(input);
        const blob = doc.output('blob');
        const url = URL.createObjectURL(blob);
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        setPdfUrl(url);
      } catch (err) {
        console.error('Auto-preview failed', err);
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showGenModal, genEmployeeId, genCompanyId, genCadence, genAnchor,
    selectedBonusIds, selectedExpenseIds, genBonuses, genExpenses, genGross, loadingItems,
    genHours,
  ]);

  async function handleDownload() {
    try {
      const input = buildPdfInput();
      if (!input || !genEmployee || !genCompany) return;

      // Persist the statement first so we have its number on the PDF.
      const { data, error } = await supabase
        .from('employee_statements')
        .insert({
          profile_id: genEmployee.id,
          company_id: genCompany.id,
          employee_name: input.employeeName,
          employee_role: input.employeeRole,
          employee_company_name: input.employeeCompany || null,
          company_snapshot: input.company,
          period_start: isoDate(input.periodStart),
          period_end: isoDate(input.periodEnd),
          pay_frequency: input.payFrequency,
          pay_type: input.payType,
          base_salary: input.payType === 'salary' ? input.baseAmount : null,
          commission_percent: input.commissionPercent,
          hourly_rate: input.hourlyRate,
          total_hours: input.totalHours,
          hours_snapshot: input.hoursSnapshot,
          gross_amount: input.grossAmount,
          base_amount: input.baseAmount,
          total_bonuses: input.totalBonuses,
          total_expenses: input.totalExpenses,
          net_pay: input.netPay,
          bonuses_snapshot: input.bonuses,
          expenses_snapshot: input.expenses,
          created_by: profile?.id || null,
        })
        .select('statement_number')
        .single();
      if (error) throw error;

      const withNumber: PdfInput = { ...input, statementNumber: data?.statement_number };
      const doc = renderPdf(withNumber);
      const name = genEmployee.full_name.replace(/\s+/g, '_');
      const range = `${isoDate(genPeriod.start)}_${isoDate(genPeriod.end)}`;
      doc.save(`statement_${data?.statement_number || ''}_${name}_${range}.pdf`);
      toast.success(`Statement #${data?.statement_number} saved`);
      loadStatements();
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Failed to download');
    }
  }

  function reprintStatement(s: GeneratedStatement) {
    if (!s.company_snapshot) {
      toast.error('This statement is missing company info.');
      return;
    }
    const doc = renderPdf({
      statementNumber: s.statement_number,
      employeeName: s.employee_name,
      employeeRole: s.employee_role,
      employeeCompany: s.employee_company_name,
      company: s.company_snapshot,
      periodStart: new Date(s.period_start + 'T00:00:00'),
      periodEnd: new Date(s.period_end + 'T00:00:00'),
      payType: s.pay_type,
      payFrequency: s.pay_frequency,
      commissionPercent: s.commission_percent,
      hourlyRate: s.hourly_rate,
      totalHours: s.total_hours,
      hoursSnapshot: (s.hours_snapshot || []).map(h => ({ date: h.date, hours: Number(h.hours) })),
      grossAmount: Number(s.gross_amount || 0),
      baseAmount: Number(s.base_amount || 0),
      bonuses: (s.bonuses_snapshot || []).map(b => ({ ...b, amount: Number(b.amount) })),
      expenses: (s.expenses_snapshot || []).map(x => ({ ...x, amount: Number(x.amount) })),
      totalBonuses: Number(s.total_bonuses || 0),
      totalExpenses: Number(s.total_expenses || 0),
      netPay: Number(s.net_pay || 0),
      generatedAt: new Date(s.created_at),
    });
    const name = s.employee_name.replace(/\s+/g, '_');
    doc.save(`statement_${s.statement_number}_${name}_${s.period_start}_${s.period_end}.pdf`);
  }

  async function deleteStatement(s: GeneratedStatement) {
    if (!confirm(`Delete statement #${s.statement_number}?`)) return;
    try {
      const { error } = await supabase.from('employee_statements').delete().eq('id', s.id);
      if (error) throw error;
      toast.success('Statement deleted');
      loadStatements();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete');
    }
  }

  function closeGen() {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setShowGenModal(false);
  }

  const totals = genEmployee ? computeTotals() : null;

  const filteredStatements = useMemo(() => {
    const q = statementsSearch.trim().toLowerCase();
    if (!q) return statements;
    return statements.filter(s =>
      s.employee_name.toLowerCase().includes(q) ||
      (s.employee_role || '').toLowerCase().includes(q) ||
      (s.company_snapshot?.name || '').toLowerCase().includes(q) ||
      String(s.statement_number).includes(q)
    );
  }, [statements, statementsSearch]);

  interface PeriodBucket {
    key: string;
    label: string;
    subtotal: number;
    statements: GeneratedStatement[];
  }

  interface EmployeeGroup {
    profileId: string;
    employeeName: string;
    employeeRole: string | null;
    totalNet: number;
    statementCount: number;
    buckets: PeriodBucket[];
  }

  const groupedStatements = useMemo<EmployeeGroup[]>(() => {
    const empMap = new Map<string, {
      name: string;
      role: string | null;
      total: number;
      count: number;
      buckets: Map<string, { label: string; sortKey: number; subtotal: number; statements: GeneratedStatement[] }>;
    }>();

    filteredStatements.forEach(s => {
      // profile_id is nulled on user deletion (ON DELETE SET NULL) — fall back to the
      // snapshotted employee_name so the statement still groups under the right person.
      const empKey = s.profile_id ?? `deleted:${s.employee_name}`;
      if (!empMap.has(empKey)) {
        empMap.set(empKey, {
          name: s.employee_name + (s.profile_id ? '' : ' (deleted user)'),
          role: s.employee_role,
          total: 0,
          count: 0,
          buckets: new Map(),
        });
      }
      const emp = empMap.get(empKey)!;
      emp.total += Number(s.net_pay || 0);
      emp.count += 1;

      const periodDate = new Date(s.period_start + 'T00:00:00');
      let bucketKey: string;
      let bucketLabel: string;
      let sortKey: number;

      if (generatedView === 'week') {
        const weekStart = startOfWeek(periodDate);
        const weekEnd = addDays(weekStart, 6);
        bucketKey = isoDate(weekStart);
        bucketLabel = fmtRange(weekStart, weekEnd);
        sortKey = weekStart.getTime();
      } else {
        const mStart = startOfMonth(periodDate);
        bucketKey = `${mStart.getFullYear()}-${String(mStart.getMonth() + 1).padStart(2, '0')}`;
        bucketLabel = mStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        sortKey = mStart.getTime();
      }

      if (!emp.buckets.has(bucketKey)) {
        emp.buckets.set(bucketKey, { label: bucketLabel, sortKey, subtotal: 0, statements: [] });
      }
      const b = emp.buckets.get(bucketKey)!;
      b.subtotal += Number(s.net_pay || 0);
      b.statements.push(s);
    });

    const groups: EmployeeGroup[] = Array.from(empMap.entries()).map(([profileId, info]) => {
      const buckets: PeriodBucket[] = Array.from(info.buckets.entries())
        .map(([key, b]) => ({ key, label: b.label, subtotal: b.subtotal, statements: b.statements }))
        .sort((a, b) => {
          const aSort = info.buckets.get(a.key)!.sortKey;
          const bSort = info.buckets.get(b.key)!.sortKey;
          return bSort - aSort;
        });
      return {
        profileId,
        employeeName: info.name,
        employeeRole: info.role,
        totalNet: info.total,
        statementCount: info.count,
        buckets,
      };
    });

    groups.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
    return groups;
  }, [filteredStatements, generatedView]);

  function toggleEmployeeCollapse(profileId: string) {
    setCollapsedEmployees(prev => {
      const next = new Set(prev);
      if (next.has(profileId)) next.delete(profileId);
      else next.add(profileId);
      return next;
    });
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileText className="h-6 w-6 text-blue-600" />
            Employee Statements
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Generate pay statements for office employees (Dispatch, Safety, Accounting, Recruiting…).
          </p>
        </div>
        <button
          onClick={() => openGenerate()}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <FileText className="h-4 w-4" /> Generate Statement
        </button>
      </div>

      <div className="flex items-center gap-1 mb-4 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('employees')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === 'employees' ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Employees
        </button>
        <button
          onClick={() => setActiveTab('generated')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 ${activeTab === 'generated' ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          <Archive className="h-4 w-4" />
          Generated Statements
          {statements.length > 0 && (
            <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${activeTab === 'generated' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-700'}`}>
              {statements.length}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'employees' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
            <h2 className="font-semibold text-slate-800">Employees</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search employees..."
                className="pl-9 pr-3 py-1.5 rounded-lg border border-slate-300 text-sm"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Role</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Company</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Paid By</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Pay Type</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 uppercase">Salary / %</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Frequency</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500">No employees found.</td></tr>
                )}
                {!loading && filtered.map(e => (
                  <tr key={e.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-sm text-slate-800">{e.full_name}</td>
                    <td className="px-4 py-2 text-sm text-slate-600">{e.role}</td>
                    <td className="px-4 py-2 text-sm text-slate-600">
                      {e.pay_company_name || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2 text-sm text-slate-600">
                      {companies.find(c => c.id === e.paying_company_id)?.name || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2 text-sm text-slate-600">
                      {e.pay_type
                        ? (e.pay_type === 'salary'
                            ? 'Salary'
                            : e.pay_type === 'percentage'
                              ? 'Percentage'
                              : 'Hourly')
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2 text-sm text-right text-slate-700">
                      {e.pay_type === 'salary' && e.base_salary != null && `$${Number(e.base_salary).toFixed(2)}`}
                      {e.pay_type === 'percentage' && e.commission_percent != null && `${Number(e.commission_percent)}%`}
                      {e.pay_type === 'hourly' && e.hourly_rate != null && `$${Number(e.hourly_rate).toFixed(2)}/hr`}
                      {!e.pay_type && <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2 text-sm text-slate-600">
                      {e.pay_frequency || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => openConfig(e)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-slate-300 text-xs text-slate-700 hover:bg-slate-50"
                        >
                          <Edit2 className="h-3 w-3" /> Configure Pay
                        </button>
                        <button
                          onClick={() => openGenerate(e.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-blue-600 text-xs text-white hover:bg-blue-700"
                        >
                          <FileText className="h-3 w-3" /> Generate
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'generated' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
            <h2 className="font-semibold text-slate-800">Generated Statements</h2>
            <div className="flex items-center gap-3">
              <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
                <button
                  onClick={() => setGeneratedView('week')}
                  className={`px-3 py-1.5 text-xs font-medium ${generatedView === 'week' ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                >
                  Weekly
                </button>
                <button
                  onClick={() => setGeneratedView('month')}
                  className={`px-3 py-1.5 text-xs font-medium border-l border-slate-300 ${generatedView === 'month' ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                >
                  Monthly
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  value={statementsSearch}
                  onChange={e => setStatementsSearch(e.target.value)}
                  placeholder="Search by #, employee, or company..."
                  className="pl-9 pr-3 py-1.5 rounded-lg border border-slate-300 text-sm"
                />
              </div>
            </div>
          </div>

          <div className="p-4 space-y-3">
            {statementsLoading && (
              <div className="text-center py-8 text-sm text-slate-500">Loading…</div>
            )}
            {!statementsLoading && groupedStatements.length === 0 && (
              <div className="text-center py-8 text-sm text-slate-500">No statements generated yet.</div>
            )}
            {!statementsLoading && groupedStatements.map(group => {
              const isCollapsed = collapsedEmployees.has(group.profileId);
              return (
                <div key={group.profileId} className="rounded-lg border border-slate-200 bg-white shadow-sm">
                  <button
                    onClick={() => toggleEmployeeCollapse(group.profileId)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {isCollapsed
                        ? <ChevronRight className="h-4 w-4 text-slate-500" />
                        : <ChevronDown className="h-4 w-4 text-slate-500" />}
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-slate-800">{group.employeeName}</div>
                        <div className="text-xs text-slate-500">
                          {group.employeeRole || '—'} · {group.statementCount} statement{group.statementCount !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500 uppercase">Total Net</div>
                      <div className="font-bold text-emerald-700">${group.totalNet.toFixed(2)}</div>
                    </div>
                  </button>

                  {!isCollapsed && (
                    <div className="border-t border-slate-200 divide-y divide-slate-100">
                      {group.buckets.map(bucket => (
                        <div key={bucket.key} className="bg-slate-50/40">
                          <div className="flex items-center justify-between px-4 py-2 bg-slate-100/60">
                            <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 uppercase">
                              <Calendar className="h-3.5 w-3.5" />
                              {bucket.label}
                              <span className="text-slate-400 normal-case">
                                ({bucket.statements.length})
                              </span>
                            </div>
                            <div className="text-sm font-semibold text-emerald-700">
                              ${bucket.subtotal.toFixed(2)}
                            </div>
                          </div>
                          <table className="min-w-full">
                            <thead>
                              <tr className="text-xs text-slate-500">
                                <th className="px-4 py-1.5 text-left font-medium">#</th>
                                <th className="px-4 py-1.5 text-left font-medium">Period</th>
                                <th className="px-4 py-1.5 text-left font-medium">Company</th>
                                <th className="px-4 py-1.5 text-left font-medium">Type</th>
                                <th className="px-4 py-1.5 text-right font-medium">Net Pay</th>
                                <th className="px-4 py-1.5 text-left font-medium">Created</th>
                                <th className="px-4 py-1.5 text-right font-medium">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {bucket.statements.map(s => (
                                <tr key={s.id} className="hover:bg-white">
                                  <td className="px-4 py-2 text-sm font-medium text-slate-800">#{s.statement_number}</td>
                                  <td className="px-4 py-2 text-sm text-slate-600">{s.period_start} → {s.period_end}</td>
                                  <td className="px-4 py-2 text-sm text-slate-600">{s.company_snapshot?.name || '—'}</td>
                                  <td className="px-4 py-2 text-sm text-slate-600">
                                    {s.pay_type === 'percentage'
                                      ? `${Number(s.commission_percent || 0)}%`
                                      : s.pay_type === 'hourly'
                                        ? `$${Number(s.hourly_rate || 0).toFixed(2)}/hr · ${Number(s.total_hours || 0).toFixed(2)} hrs`
                                        : 'Salary'}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-right font-semibold text-emerald-700">
                                    ${Number(s.net_pay).toFixed(2)}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-slate-500">
                                    {new Date(s.created_at).toLocaleDateString()}
                                  </td>
                                  <td className="px-4 py-2 text-right">
                                    <div className="inline-flex gap-1">
                                      <button
                                        onClick={() => reprintStatement(s)}
                                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 text-xs text-slate-700 hover:bg-slate-50 bg-white"
                                        title="Download PDF"
                                      >
                                        <Download className="h-3 w-3" /> PDF
                                      </button>
                                      <button
                                        onClick={() => deleteStatement(s)}
                                        className="p-1.5 rounded hover:bg-red-50 text-red-600"
                                        title="Delete"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Generate Statement Modal */}
      {showGenModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" /> Generate Employee Statement
              </h2>
              <button onClick={closeGen} className="p-1 rounded hover:bg-slate-200">
                <X className="h-5 w-5 text-slate-600" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 h-full">
                {/* Left: Form */}
                <div className="p-5 space-y-5 border-r border-slate-200">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase">Employee *</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                      <input
                        type="text"
                        value={genEmployeeSearch}
                        onChange={e => {
                          setGenEmployeeSearch(e.target.value);
                          setGenEmployeeOpen(true);
                          if (genEmployeeId) setGenEmployeeId('');
                          setPdfUrl(null);
                        }}
                        onFocus={() => setGenEmployeeOpen(true)}
                        onBlur={() => setTimeout(() => setGenEmployeeOpen(false), 150)}
                        placeholder="Search employee by name or role..."
                        className="w-full pl-9 pr-8 py-2 rounded-lg border border-slate-300"
                      />
                      {genEmployeeSearch && (
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setGenEmployeeId('');
                            setGenEmployeeSearch('');
                            setGenEmployeeOpen(true);
                            setPdfUrl(null);
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100"
                          title="Clear"
                        >
                          <X className="h-3.5 w-3.5 text-slate-500" />
                        </button>
                      )}
                      {genEmployeeOpen && (() => {
                        const q = genEmployeeSearch.trim().toLowerCase();
                        const matches = employees.filter(e =>
                          !q ||
                          e.full_name.toLowerCase().includes(q) ||
                          e.role.toLowerCase().includes(q)
                        );
                        return (
                          <div className="absolute z-10 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-56 overflow-y-auto">
                            {matches.length === 0 && (
                              <div className="p-3 text-xs text-slate-500">No employees match.</div>
                            )}
                            {matches.map(emp => (
                              <button
                                key={emp.id}
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); selectGenEmployee(emp); }}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${genEmployeeId === emp.id ? 'bg-blue-50' : ''}`}
                              >
                                <div className="flex justify-between items-baseline">
                                  <span className="font-medium text-slate-800">{emp.full_name}</span>
                                  <span className="text-xs text-slate-500">{emp.role}</span>
                                </div>
                                <div className="text-xs text-slate-500">
                                  {emp.pay_type
                                    ? (emp.pay_type === 'percentage'
                                        ? `${Number(emp.commission_percent || 0)}% · ${emp.pay_frequency}`
                                        : emp.pay_type === 'hourly'
                                          ? `$${Number(emp.hourly_rate || 0).toFixed(2)}/hr · ${emp.pay_frequency}`
                                          : `$${Number(emp.base_salary || 0).toFixed(2)} · ${emp.pay_frequency}`)
                                    : <span className="text-amber-600">no pay config</span>}
                                </div>
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                    {genEmployee && !genEmployee.pay_type && (
                      <p className="mt-1 text-xs text-amber-600">
                        This employee has no pay config. Close this modal and click "Configure Pay".
                      </p>
                    )}
                    {genEmployee?.pay_type && (
                      <p className="mt-1 text-xs text-slate-500">
                        {genEmployee.pay_type === 'percentage'
                          ? `${Number(genEmployee.commission_percent || 0)}% of dispatcher gross · ${genEmployee.pay_frequency}`
                          : genEmployee.pay_type === 'hourly'
                            ? `Hourly $${Number(genEmployee.hourly_rate || 0).toFixed(2)}/hr · ${genEmployee.pay_frequency}`
                            : `Salary $${Number(genEmployee.base_salary || 0).toFixed(2)} / ${genEmployee.pay_frequency}`}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase">Paying Company *</label>
                    <select
                      value={genCompanyId}
                      onChange={e => { setGenCompanyId(e.target.value); setPdfUrl(null); }}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2"
                    >
                      <option value="">Select company…</option>
                      {companies.map(c => (
                        <option key={c.id} value={c.id}>{c.name}{c.code ? ` (${c.code})` : ''}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase">Period</label>
                    <div className="flex items-center gap-2 mb-2">
                      <select
                        value={genCadence}
                        onChange={e => { setGenCadence(e.target.value as PayFrequency); setPdfUrl(null); }}
                        className="rounded-lg border border-slate-300 px-3 py-2"
                      >
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Every 2 weeks</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setGenAnchor(shiftPeriod(genAnchor, genCadence, -1)); setPdfUrl(null); }}
                        className="p-2 rounded-lg border border-slate-300 hover:bg-slate-50"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <div className="flex-1 px-3 py-2 rounded-lg border border-slate-300 bg-slate-50 text-sm font-medium text-slate-700 text-center">
                        {fmtRange(genPeriod.start, genPeriod.end)}
                      </div>
                      <button
                        onClick={() => { setGenAnchor(shiftPeriod(genAnchor, genCadence, 1)); setPdfUrl(null); }}
                        className="p-2 rounded-lg border border-slate-300 hover:bg-slate-50"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Hours per day — only when employee is paid hourly */}
                  {genEmployee?.pay_type === 'hourly' && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-semibold text-slate-600 uppercase">
                          Hours per day
                        </label>
                        <span className="text-xs text-slate-500">
                          Rate: ${Number(genEmployee.hourly_rate || 0).toFixed(2)}/hr
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2 rounded-lg border border-slate-200 p-2">
                        {genPeriodDays.map(iso => {
                          const d = new Date(iso + 'T00:00:00');
                          const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
                          const dayMonth = d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
                          return (
                            <label key={iso} className="flex flex-col gap-1">
                              <span className="text-[10px] font-medium text-slate-500 uppercase">
                                {weekday} {dayMonth}
                              </span>
                              <input
                                type="number"
                                step="0.25"
                                min="0"
                                max="24"
                                value={genHours[iso] || ''}
                                onChange={e => setGenHours(prev => ({ ...prev, [iso]: e.target.value }))}
                                placeholder="0"
                                className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm text-right"
                              />
                            </label>
                          );
                        })}
                      </div>
                      <div className="mt-1 text-xs text-slate-500 flex justify-between">
                        <span>Total hours: <span className="font-semibold text-slate-700">{genTotalHours.toFixed(2)}</span></span>
                        <span>
                          Base pay: <span className="font-semibold text-slate-700">
                            ${(genTotalHours * Number(genEmployee.hourly_rate || 0)).toFixed(2)}
                          </span>
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Bonuses */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-semibold text-slate-600 uppercase">Bonuses in period</label>
                      <button
                        onClick={() => setSelectedBonusIds(new Set(genBonuses.map(b => b.id)))}
                        className="text-xs text-blue-600 hover:underline"
                        disabled={genBonuses.length === 0}
                      >
                        Select all
                      </button>
                    </div>
                    <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-44 overflow-y-auto">
                      {loadingItems && (
                        <div className="p-3 text-xs text-slate-500">Loading…</div>
                      )}
                      {!loadingItems && genBonuses.length === 0 && (
                        <div className="p-3 text-xs text-slate-500">No bonuses in this period.</div>
                      )}
                      {!loadingItems && genBonuses.map(b => (
                        <label key={b.id} className="flex items-center gap-2 p-2 text-sm hover:bg-slate-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedBonusIds.has(b.id)}
                            onChange={() => toggleBonus(b.id)}
                          />
                          <span className="flex-1 text-slate-700 text-xs">
                            <span className="text-slate-500">{b.bonus_date}</span>
                            {' · '}
                            {b.description || '—'}
                          </span>
                          <span className="text-xs font-medium text-green-700">
                            +${Number(b.amount).toFixed(2)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Expenses */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-semibold text-slate-600 uppercase">Expenses in period</label>
                      <button
                        onClick={() => setSelectedExpenseIds(new Set(genExpenses.map(x => x.id)))}
                        className="text-xs text-blue-600 hover:underline"
                        disabled={genExpenses.length === 0}
                      >
                        Select all
                      </button>
                    </div>
                    <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-44 overflow-y-auto">
                      {loadingItems && (
                        <div className="p-3 text-xs text-slate-500">Loading…</div>
                      )}
                      {!loadingItems && genExpenses.length === 0 && (
                        <div className="p-3 text-xs text-slate-500">No expenses in this period.</div>
                      )}
                      {!loadingItems && genExpenses.map(x => (
                        <label key={x.id} className="flex items-center gap-2 p-2 text-sm hover:bg-slate-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedExpenseIds.has(x.id)}
                            onChange={() => toggleExpense(x.id)}
                          />
                          <span className="flex-1 text-slate-700 text-xs">
                            <span className="text-slate-500">{x.expense_date}</span>
                            {' · '}
                            {x.description || '—'}
                          </span>
                          <span className="text-xs font-medium text-red-700">
                            -${Number(x.amount).toFixed(2)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Totals summary */}
                  {totals && (
                    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
                      {genEmployee?.pay_type === 'percentage' && (
                        <div className="flex justify-between text-xs text-slate-600">
                          <span>Dispatcher Gross</span>
                          <span>${genGross.toFixed(2)}</span>
                        </div>
                      )}
                      {genEmployee?.pay_type === 'hourly' && (
                        <div className="flex justify-between text-xs text-slate-600">
                          <span>Hours × Rate</span>
                          <span>{genTotalHours.toFixed(2)} × ${Number(genEmployee.hourly_rate || 0).toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-slate-700">
                          {genEmployee?.pay_type === 'percentage'
                            ? 'Commission'
                            : genEmployee?.pay_type === 'hourly'
                              ? 'Hourly Pay'
                              : 'Base Salary'}
                        </span>
                        <span className="font-medium text-slate-800">${totals.baseAmount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-700">Bonuses</span>
                        <span className="font-medium text-green-700">+${totals.totalBonuses.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-700">Expenses</span>
                        <span className="font-medium text-red-700">-${totals.totalExpenses.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between pt-2 mt-1 border-t border-slate-200">
                        <span className="font-semibold text-slate-800">Net Pay</span>
                        <span className="font-bold text-emerald-700">${totals.netPay.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right: PDF Preview */}
                <div className="p-5 bg-slate-100 flex flex-col min-h-[500px]">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-800 text-sm uppercase flex items-center gap-2">
                      <Eye className="h-4 w-4" /> Preview
                      {loadingItems && <span className="text-xs font-normal text-slate-500">updating…</span>}
                    </h3>
                    <button
                      onClick={handleDownload}
                      disabled={!genEmployee || !genCompany}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs hover:bg-blue-700 disabled:opacity-50"
                      title="Save to Generated Statements and download PDF"
                    >
                      <Download className="h-3 w-3" /> Save & Download
                    </button>
                  </div>
                  <div className="flex-1 rounded-lg overflow-hidden bg-white border border-slate-300">
                    {pdfUrl ? (
                      <iframe src={pdfUrl} title="statement-preview" className="w-full h-full min-h-[500px]" />
                    ) : (
                      <div className="h-full min-h-[500px] flex items-center justify-center text-sm text-slate-500 p-6 text-center">
                        Select an employee and a paying company — the PDF will appear here automatically.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pay config modal */}
      {showConfigModal && configEmp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Configure Pay</h2>
                <p className="text-xs text-slate-500">{configEmp.full_name} — {configEmp.role}</p>
              </div>
              <button onClick={() => setShowConfigModal(false)} className="p-1 rounded hover:bg-slate-100">
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Paying Company</label>
                <select
                  value={configForm.paying_company_id}
                  onChange={e => setConfigForm(f => ({ ...f, paying_company_id: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                >
                  <option value="">Select company…</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.name}{c.code ? ` (${c.code})` : ''}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-slate-500">
                  Which of our companies pays this employee. Statements are issued from this company — it's preselected when you generate a statement.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Contractor Company Name</label>
                <input
                  type="text"
                  value={configForm.pay_company_name}
                  onChange={e => setConfigForm(f => ({ ...f, pay_company_name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  placeholder="e.g. Brkljac Logistics LLC"
                />
                <p className="mt-1 text-xs text-slate-500">
                  If this employee works as a contractor under their own company, type it here — the statement will be made out to this company name. Leave blank to pay under their personal name.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Pay Type *</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setConfigForm(f => ({ ...f, pay_type: 'salary' }))}
                    className={`px-3 py-2 rounded-lg border text-sm ${configForm.pay_type === 'salary' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                  >
                    <DollarSign className="inline h-4 w-4 mr-1" /> Fixed Salary
                  </button>
                  <button
                    onClick={() => setConfigForm(f => ({ ...f, pay_type: 'percentage' }))}
                    className={`px-3 py-2 rounded-lg border text-sm ${configForm.pay_type === 'percentage' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                  >
                    <Percent className="inline h-4 w-4 mr-1" /> % of Gross
                  </button>
                  <button
                    onClick={() => setConfigForm(f => ({ ...f, pay_type: 'hourly' }))}
                    className={`px-3 py-2 rounded-lg border text-sm ${configForm.pay_type === 'hourly' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                  >
                    <Calendar className="inline h-4 w-4 mr-1" /> Per Hour
                  </button>
                </div>
                {configForm.pay_type === 'percentage' && (
                  <p className="mt-1 text-xs text-slate-500">
                    Commission is computed against this user's dispatcher gross — the sum of <code>loads.total_rate</code> for loads they booked in the period.
                  </p>
                )}
                {configForm.pay_type === 'hourly' && (
                  <p className="mt-1 text-xs text-slate-500">
                    Base pay is <code>hourly_rate × hours</code>. When generating a statement, enter the hours worked per day.
                  </p>
                )}
              </div>

              {configForm.pay_type === 'salary' && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Salary per period *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="number"
                      step="0.01"
                      value={configForm.base_salary}
                      onChange={e => setConfigForm(f => ({ ...f, base_salary: e.target.value }))}
                      className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              )}

              {configForm.pay_type === 'percentage' && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Commission percent * (e.g. 3 for 3%)
                  </label>
                  <div className="relative">
                    <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="number"
                      step="0.001"
                      value={configForm.commission_percent}
                      onChange={e => setConfigForm(f => ({ ...f, commission_percent: e.target.value }))}
                      className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300"
                      placeholder="3"
                    />
                  </div>
                </div>
              )}

              {configForm.pay_type === 'hourly' && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Hourly rate * ($ per hour)
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="number"
                      step="0.01"
                      value={configForm.hourly_rate}
                      onChange={e => setConfigForm(f => ({ ...f, hourly_rate: e.target.value }))}
                      className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300"
                      placeholder="25.00"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Pay Frequency *</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['weekly', 'biweekly', 'monthly'] as PayFrequency[]).map(f => (
                    <button
                      key={f}
                      onClick={() => setConfigForm(cf => ({ ...cf, pay_frequency: f }))}
                      className={`px-3 py-2 rounded-lg border text-sm capitalize ${configForm.pay_frequency === f ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                    >
                      <Calendar className="inline h-4 w-4 mr-1" />
                      {f === 'biweekly' ? 'Every 2 wks' : f}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowConfigModal(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
              <button onClick={saveConfig} className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
